import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useCallback, useMemo } from 'react';
import { loginRequest, apiTokenRequest } from './msalConfig';

export interface AuthUser {
  /** Display name from the identity token */
  name: string;
  /** Email/UPN from the identity token */
  email: string;
  /** Subject (oid/sub) from the identity token */
  subject: string;
}

export interface UseAuthReturn {
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** Current user identity, null if not authenticated */
  user: AuthUser | null;
  /** Initiate login via MSAL redirect */
  login: () => Promise<void>;
  /** Logout and redirect to EntraID logout endpoint */
  logout: () => Promise<void>;
  /** Acquire an access token silently (falls back to interactive if needed) */
  getAccessToken: () => Promise<string>;
}

/**
 * Hook exposing authentication state and actions.
 *
 * - `isAuthenticated`: current auth state
 * - `user`: { name, subject } from the active account
 * - `login()`: initiate OIDC login
 * - `logout()`: end session
 * - `getAccessToken()`: silent token acquisition with automatic renewal
 */
export function useAuth(): UseAuthReturn {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const user: AuthUser | null = useMemo(() => {
    if (!isAuthenticated || accounts.length === 0) return null;
    const account = accounts[0];
    return {
      name: account.name ?? account.username ?? '',
      email: account.username ?? '',
      subject: account.localAccountId ?? account.homeAccountId ?? '',
    };
  }, [isAuthenticated, accounts]);

  const login = useCallback(async () => {
    await instance.loginRedirect({
      ...loginRequest,
      redirectStartPage: window.location.href,
    });
  }, [instance]);

  const logout = useCallback(async () => {
    await instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      throw new Error('No active account. User must log in first.');
    }

    try {
      // Attempt silent acquisition of an ACCESS token scoped to the BFF API
      // (audience = this app), so the Authorizer/BFF accept it.
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest(),
        account,
      });
      return response.accessToken;
    } catch {
      // Silent acquisition failed — trigger interactive login
      await instance.loginRedirect({
        ...apiTokenRequest(),
        redirectStartPage: window.location.href,
      });
      // This will redirect, so we won't reach here in normal flow
      throw new Error('Redirecting to login for token renewal.');
    }
  }, [instance, accounts]);

  return {
    isAuthenticated,
    user,
    login,
    logout,
    getAccessToken,
  };
}
